import { useCallback, useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';
import { teamsClient } from '@atlaskit/teams-client/client';
import { logException } from '@atlaskit/teams-client/sentry';

import { parseUserAriToUserId } from './utils';

const DISPLAY_USER_COUNT = 12;

export interface TeamUser {
	id: string;
	name: string;
	picture: string;
	email?: string;
	jobTitle?: string;
	location?: string;
}

interface CollaborationGraphUser {
	id: string;
	expand: {
		name: string;
		picture: string;
		nickname: string;
		account_status: string;
		extended_profile?: {
			job_title?: string;
			organization?: string;
			department?: string;
			location?: string;
		};
	};
}

interface CollaboratorsResponsePayload {
	recommendedEntities: CollaborationGraphUser[];
}

export interface TeamCollabResponse {
	data: TeamUser[];
	loading: boolean;
	error?: Error;
	refetch: () => Promise<void>;
}

const convertAvatarResolution = (avatarUrl: string, size: number = 96): string => {
	if (!avatarUrl) {
		return '';
	}
	return avatarUrl.replace(/\/\d+$/, `/${size}`);
};

const transformCollaborators = (data: CollaboratorsResponsePayload | undefined): TeamUser[] => {
	if (!data) {
		return [];
	}
	return data.recommendedEntities
		.filter((user) => user.expand.account_status === 'active')
		.map((user) => {
			const { expand } = user;

			return {
				id: parseUserAriToUserId(user.id),
				name: expand.nickname ? expand.nickname : expand.name,
				picture: convertAvatarResolution(expand.picture, 96),
				jobTitle: expand.extended_profile?.job_title,
				location: expand.extended_profile?.location,
			};
		});
};

const fetchCollaborators = async (
	accountId: string,
	cloudId: string,
	maxNumberOfResults: number,
): Promise<CollaboratorsResponsePayload> => {
	return teamsClient.getRecommendedUsers({
		accountId,
		cloudId,
		maxNumberOfResults,
	});
};

export const useTeamCollabQuery = (accountId: string, cloudId: string): TeamCollabResponse => {
	const [data, setData] = useState<TeamUser[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const maxResultsToFetchRef = useRef<number>(DISPLAY_USER_COUNT);
	const { fireEvent } = useAnalyticsEvents();

	const loadCollaborators = useCallback(
		async (isRefetch: boolean = false) => {
			if (!accountId || !cloudId) {
				setLoading(false);
				return;
			}

			try {
				setLoading(true);
				setError(undefined);
				if (isRefetch) {
					maxResultsToFetchRef.current += DISPLAY_USER_COUNT;
				}

				const collaborators = await fetchCollaborators(
					accountId,
					cloudId,
					maxResultsToFetchRef.current,
				);
				const newUsers = transformCollaborators(collaborators);

				setData((prevData) => {
					if (isRefetch) {
						const existingIds = new Set(prevData.map((user) => user.id));
						const uniqueNewUsers = newUsers.filter((user) => !existingIds.has(user.id));
						return [...prevData, ...uniqueNewUsers];
					}
					return newUsers;
				});

				fireEvent('operational.teamSuggestionsRecommendedUsers.succeeded', {
					recommendedUsers: newUsers.length,
				});
			} catch (err) {
				const error = err instanceof Error ? err : new Error(String(err));
				setError(error);
				logException(error, 'useTeamCollabQuery.fetchCollaborators', {
					accountId,
					cloudId,
				});

				fireEvent('operational.teamSuggestionsRecommendedUsers.failed', {
					recommendedUsers: 0,
					errors: [error?.message ?? 'Failed to fetch recommended users'],
				});
			} finally {
				setLoading(false);
			}
		},
		[accountId, cloudId, fireEvent],
	);

	useEffect(() => {
		loadCollaborators(false);
	}, [loadCollaborators]);

	const refetch = useCallback(async () => {
		await loadCollaborators(true);
	}, [loadCollaborators]);

	return { data, loading, error, refetch };
};
